import React, { useEffect, useMemo, useState } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import { useParams } from "react-router-dom";
import {
  FiActivity,
  FiAlertCircle,
  FiBarChart2,
  FiCalendar,
  FiCheckCircle,
  FiDollarSign,
  FiEye,
  FiPrinter,
  FiRefreshCw,
  FiSearch,
} from "react-icons/fi";
import pt from "date-fns/locale/pt";

import "react-datepicker/dist/react-datepicker.css";
import "./HistoricoFechamento.css";

import Footer from "../../../components/Footer/Footer";
import { Loading } from "../../../components/Loading";
import { LoadingButton } from "../../../components/loadingButtom";
import { ModalDesconectar } from "../../../components/modais/ModalDesconectar";
import { ModalHistoricoFechamento, Cotacoes } from "../../../components/modais/ModalHistoricoFechamento";
import Navbar from "../../../components/Navbar/Navbar";
import { TabelaHistoricoFechamento } from "../../../components/Tabelas/TabelaHistoricoFechamento";
import { DateFormatter } from "../../../functions/DateFormatter";
import { mascaraBRL, mascaraUSD } from "../../../functions/MascarasMonetarias";
import {
  criaPDFHistoricoFechamento,
  obterFundoFinaceiroPrivate,
  obterIdFundoFinanceiroAcessoria,
} from "./functions";

registerLocale("pt", pt);

export interface Responsavel {
  idCredenciais: string;
  nome: string;
}

export interface Fechamentos {
  id: number;
  data: Date;
  valorTotalDolar: number;
  valorTotalReal: number;
  valorCota: number;
  ativosFinanceiros: Cotacoes[];
  responsavel: Responsavel;
  rendimentoDiario: number;
}

interface HistoricoMobileCardProps {
  fechamento: Fechamentos;
  clientePrivate: boolean;
}

const HistoricoMobileCard = ({ fechamento, clientePrivate }: HistoricoMobileCardProps) => {
  const [modalFechamento, setModalFechamento] = useState<boolean>(false);

  return (
    <article className="historico-fechamento-mobile-card">
      <div className="historico-fechamento-mobile-card-header">
        <span>{DateFormatter(fechamento.data)}</span>
        <button
          type="button"
          onClick={() => setModalFechamento(true)}
          aria-label={`Ver fechamento de ${DateFormatter(fechamento.data)}`}
        >
          <FiEye />
        </button>
      </div>
      <div className="historico-fechamento-mobile-card-body">
        <div>
          <small>Valor</small>
          <strong>
            {clientePrivate
              ? `US$ ${mascaraUSD(fechamento.valorTotalDolar.toFixed(2))}`
              : `R$ ${mascaraBRL(fechamento.valorTotalReal.toFixed(2))}`}
          </strong>
        </div>
        <div>
          <small>Gestor</small>
          <strong>{fechamento.responsavel?.nome || "-"}</strong>
        </div>
      </div>

      {modalFechamento && (
        <ModalHistoricoFechamento
          modal={modalFechamento}
          setModal={setModalFechamento}
          cotacoes={fechamento.ativosFinanceiros}
          valorTotalReal={fechamento.valorTotalReal}
          valorTotalDolar={fechamento.valorTotalDolar}
          valorCota={fechamento.valorCota}
        />
      )}
    </article>
  );
};

const HistoricoFechamento = () => {
  const [dataInicial, setDataInicial] = useState<Date>(
    new Date(new Date().setMonth(new Date().getMonth() - 1))
  );
  const [dataFinal, setDataFinal] = useState<Date>(new Date());
  const [modalDesconectar, setModalDesconectar] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [reload, setReload] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [fechamentos, setFechamentos] = useState<Array<Fechamentos>>([]);

  const { idCredenciais, nomeCliente } = useParams();
  const clientePrivate = Boolean(idCredenciais);
  const nomeFundo = clientePrivate ? `Fundo ${nomeCliente || "Private"}` : "Fundo Assessoria";

  useEffect(() => {
    setError(false);

    if (!idCredenciais) {
      obterIdFundoFinanceiroAcessoria(
        setError,
        setModalDesconectar,
        dataInicial,
        dataFinal,
        setFechamentos,
        setLoading
      );
      return;
    }

    obterFundoFinaceiroPrivate(
      idCredenciais,
      setError,
      setModalDesconectar,
      dataInicial,
      dataFinal,
      setFechamentos,
      setLoading
    );
  }, [dataFinal, dataInicial, idCredenciais, reload]);

  const totais = useMemo(
    () =>
      fechamentos.reduce(
        (acc, fechamento) => ({
          brl: acc.brl + Number(fechamento.valorTotalReal || 0),
          usd: acc.usd + Number(fechamento.valorTotalDolar || 0),
          rendimento: acc.rendimento + Number(fechamento.rendimentoDiario || 0),
        }),
        { brl: 0, usd: 0, rendimento: 0 }
      ),
    [fechamentos]
  );

  const mediaRendimento = fechamentos.length ? totais.rendimento / fechamentos.length : 0;
  const ultimoFechamento = fechamentos[0];

  const pesquisar = () => setReload((valorAtual) => !valorAtual);

  return (
    <>
      {loading && <Loading />}
      <Navbar />

      <main className="historico-fechamento-page">
        <section className="historico-fechamento-hero">
          <div>
            <span>Histórico financeiro</span>
            <h1>Histórico de fechamento</h1>
            <p>
              Consulte fechamentos publicados por período, visualize a composição
              do dia e imprima relatórios operacionais.
            </p>
          </div>

          <div className="historico-fechamento-filter">
            <strong>{nomeFundo}</strong>
            <div className="historico-fechamento-date-grid">
              <label>
                <span>Início</span>
                <DatePicker
                  className="historico-fechamento-date"
                  maxDate={new Date(new Date().setDate(new Date().getDate() - 1))}
                  dateFormat="dd/MM/yyyy"
                  locale="pt"
                  selected={dataInicial}
                  onChange={(dataInicio: Date) => setDataInicial(dataInicio)}
                />
              </label>
              <label>
                <span>Fim</span>
                <DatePicker
                  className="historico-fechamento-date"
                  maxDate={new Date()}
                  minDate={dataInicial}
                  dateFormat="dd/MM/yyyy"
                  locale="pt"
                  selected={dataFinal}
                  onChange={(dataFim: Date) => setDataFinal(dataFim)}
                />
              </label>
            </div>
            <button type="button" onClick={pesquisar} disabled={loading}>
              <FiSearch />
              Pesquisar
            </button>
          </div>
        </section>

        <section className="historico-fechamento-summary" aria-label="Resumo do histórico">
          <article>
            <FiCalendar />
            <span>Fechamentos</span>
            <strong>{fechamentos.length}</strong>
          </article>
          <article>
            <FiDollarSign />
            <span>Total BRL</span>
            <strong>R$ {mascaraBRL(totais.brl.toFixed(2))}</strong>
          </article>
          <article>
            <FiBarChart2 />
            <span>Total USD</span>
            <strong>US$ {mascaraUSD(totais.usd.toFixed(2))}</strong>
          </article>
          <article>
            <FiActivity />
            <span>Rendimento médio</span>
            <strong>{mediaRendimento.toFixed(2)}%</strong>
          </article>
        </section>

        <section className="historico-fechamento-panel">
          <div className="historico-fechamento-panel-header">
            <div>
              <span>Extrato</span>
              <h2>Fechamentos encontrados</h2>
            </div>
            <div className="historico-fechamento-panel-actions">
              <small>
                Último: {ultimoFechamento ? DateFormatter(ultimoFechamento.data) : "-"}
              </small>
              <button
                type="button"
                onClick={() => criaPDFHistoricoFechamento()}
                disabled={!fechamentos.length}
              >
                <FiPrinter />
                Imprimir
              </button>
            </div>
          </div>

          {error ? (
            <div className="historico-fechamento-state error">
              <FiAlertCircle />
              <strong>Não foi possível carregar o histórico.</strong>
              <button type="button" onClick={pesquisar}>
                <FiRefreshCw />
                Tentar novamente
              </button>
            </div>
          ) : loading ? (
            <div className="historico-fechamento-state">
              <LoadingButton />
            </div>
          ) : fechamentos.length ? (
            <>
              <div className="historico-fechamento-table-wrap">
                <table id="tblTransacoes" className="historico-fechamento-table">
                  <thead>
                    <tr className="tabelaHistoricoFechamento">
                      <th>Data</th>
                      <th>Valor do fechamento</th>
                      <th>Gestor</th>
                      <th id="colunaFechamento">Ver fechamento</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fechamentos.map((fechamento) => (
                      <TabelaHistoricoFechamento
                        idCredenciais={idCredenciais}
                        dados={fechamento}
                        key={fechamento.id}
                      />
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="historico-fechamento-mobile-list">
                {fechamentos.map((fechamento) => (
                  <HistoricoMobileCard
                    key={fechamento.id}
                    fechamento={fechamento}
                    clientePrivate={clientePrivate}
                  />
                ))}
              </div>
            </>
          ) : (
            <div className="historico-fechamento-state">
              <FiCheckCircle />
              <strong>Nenhum dado disponível para consulta neste período.</strong>
            </div>
          )}
        </section>
      </main>

      <Footer />
      {modalDesconectar && <ModalDesconectar modal={modalDesconectar} />}
    </>
  );
};

export default HistoricoFechamento;
